import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { useParams, useNavigate } from 'react-router-dom';
import language from '../../variables/language';
import PhraseComposer from './PhraseComposer';
import phraseTypeLabel from './phraseTypeLabel';
import '../../css/phrase-composer.css';

export default function StudentPhrases() {
   const { id } = useParams();
   const navigate = useNavigate();
   const langRoot = language[language.lang];
   const lang = langRoot.phrasesTab;

   const [studentPhrases, setStudentPhrases] = useState([]);
   const [status, setStatus] = useState('loading');
   const [phraseTypes, setPhraseTypes] = useState([]);
   const [activePhraseTypeId, setActivePhraseTypeId] = useState(null);

   const load = () => {
      axios.get('/api/student-phrases', { params: { student: id }, withCredentials: true })
         .then(response => {
            setStudentPhrases(Array.isArray(response.data) ? response.data : []);
            setStatus('done');
         })
         .catch(err => {
            if (err.response && err.response.status === 401) navigate('/login');
            setStatus('done');
         });
   }

   useEffect(() => {
      load();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id]);

   // Phrase/Sentence tabs - see LessonDetailModal's identical effect for why
   // this is driven by the fetched list rather than hardcoded ids.
   useEffect(() => {
      axios.get('/api/phrase-types', { withCredentials: true })
         .then(response => {
            const types = Array.isArray(response.data) ? response.data : [];
            setPhraseTypes(types);
            setActivePhraseTypeId(prev => prev || (types.length > 0 ? types[0].id : null));
         })
         .catch(() => {});
   }, []);

   const handleRemove = (studentPhraseId) => {
      axios.delete('/api/student-phrases/' + studentPhraseId, { withCredentials: true })
         .then(() => load())
         .catch(err => console.log(err));
   }

   const visiblePhrases = studentPhrases.filter(sp => sp.phrase && sp.phrase.type && sp.phrase.type.id === activePhraseTypeId);

   // Grouped by the calendar day they were added (StudentPhrase.addedAt),
   // newest day first (top-left, flex-wrap reads left-to-right/top-to-bottom
   // like a normal feed) - a plain string sort on the "yyyy-MM-dd" key
   // already sorts chronologically, no date parsing needed for the ordering
   // itself.
   const dayColumns = useMemo(() => {
      const groups = {};
      visiblePhrases.forEach(sp => {
         if (!sp.addedAt) return;
         const dateKey = sp.addedAt.slice(0, 10);
         if (!groups[dateKey]) groups[dateKey] = [];
         groups[dateKey].push(sp);
      });
      return Object.keys(groups).sort().reverse().map(dateKey => ({ dateKey, items: groups[dateKey] }));
   }, [visiblePhrases]);

   const weekdayFull = (dateKey) => {
      const dt = DateTime.fromISO(dateKey);
      return dt.isValid ? langRoot.weekDaysFull[dt.weekday - 1] : '';
   }
   const formattedDate = (dateKey) => {
      const dt = DateTime.fromISO(dateKey);
      return dt.isValid ? dt.toFormat('dd.MM.yyyy') : dateKey;
   }

   return (
      <div className="student-lessons">
         {phraseTypes.length > 1 ? (
            <div className="phrase-type-tabs">
               {phraseTypes.map(t => (
                  <div key={t.id}
                     className={'phrase-type-tab' + (activePhraseTypeId === t.id ? ' active' : '')}
                     onClick={() => setActivePhraseTypeId(t.id)}>
                     {phraseTypeLabel(t, lang)}
                  </div>
               ))}
            </div>
         ) : ''}

         <PhraseComposer studentId={id} typeId={activePhraseTypeId} onAttached={load} />

         <div className="phrase-days-board">
            {status === 'done' && dayColumns.length === 0 ? (
               <span className="phrase-chip-list-empty">{lang.noPhrasesYet}</span>
            ) : ''}
            {dayColumns.map(col => (
               <div className="phrase-day-column" key={col.dateKey}>
                  <div className="phrase-day-header">
                     <span className="phrase-day-weekday">{weekdayFull(col.dateKey)}</span>
                     <span className="phrase-day-date">{formattedDate(col.dateKey)}</span>
                  </div>
                  <div className="phrase-day-list">
                     {col.items.map(sp => (
                        <span className="phrase-chip" key={sp.id}>
                           <span className="phrase-chip-text">
                              <span className="phrase-chip-sentence">{sp.phrase ? sp.phrase.text : ''}</span>
                              {sp.phrase && sp.phrase.translation ? <span className="phrase-chip-translation">{sp.phrase.translation}</span> : ''}
                           </span>
                           <span className="phrase-chip-side">
                              <span className="phrase-chip-remove" onClick={() => handleRemove(sp.id)}>×</span>
                              {sp.phrase && sp.phrase.tags && sp.phrase.tags.length > 0 ? (
                                 <span className="phrase-chip-tags">
                                    {sp.phrase.tags.map(t => <span className="phrase-chip-tag" key={t.id}>{t.name}</span>)}
                                 </span>
                              ) : ''}
                           </span>
                        </span>
                     ))}
                  </div>
               </div>
            ))}
         </div>
      </div>
   );
}
